import React, { useCallback } from "react";
import { styled } from "@mui/material/styles";
import {
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../helpers";
import Skeleton from "@mui/material/Skeleton";
import { useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import School from "../../assets/school-classes.json";

const PREFIX = "DashboardList";

const classes = {
  elementContainer: `${PREFIX}-elementContainer`,
  root: `${PREFIX}-root`,
  gridList: `${PREFIX}-gridList`,
  title: `${PREFIX}-title`,
  titleBar: `${PREFIX}-titleBar`,
  titleContainer: `${PREFIX}-titleContainer`,
  categoryText: `${PREFIX}-categoryText`,
  seeMoreText: `${PREFIX}-seeMoreText`,
  skeletonRoot: `${PREFIX}-skeletonRoot`,
  tileWidth: `${PREFIX}-tileWidth`,
  noResults: `${PREFIX}-noResults`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.elementContainer}`]: {
    padding: "1rem 0",
    margin: "0 0 1rem 0 ",
  },

  [`& .${classes.root}`]: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    overflow: "hidden",
    padding: "0.5rem",
    minHeight: "200px",
  },

  [`& .${classes.gridList}`]: {
    display: "flex",
    flexWrap: "nowrap",
    "&::-webkit-scrollbar": {
      width: "5px",
      height: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
    // Promote the list into his own layer on Chrome. This cost memory but helps keeping high FPS.
    transform: "translateZ(0)",
  },

  [`& .${classes.title}`]: {
    color: theme.palette.primary.light,
  },

  [`& .${classes.titleBar}`]: {
    background:
      "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)",
  },

  [`& .${classes.titleContainer}`]: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "0.5rem",
    position: "relative",
  },

  [`& .${classes.categoryText}`]: {
    fontSize: "24px",
    color: theme.palette.primary.main,
    minWidth: "100px",
  },

  [`& .${classes.seeMoreText}`]: {
    position: "absolute",
    bottom: "0",
    color: theme.palette.primary.main,
    cursor: "pointer",
    minWidth: "20px",
  },

  [`& .${classes.skeletonRoot}`]: {
    height: "200px",
  },

  [`& .${classes.tileWidth}`]: {
    width: "270px !important",
    height: "225px !important",
    objectFit: "contain !important",
  },

  [`& .${classes.noResults}`]: {
    margin: "0 auto",
    top: "4.5rem",
    position: "relative",
  },
}));

export default function DashboardList(props) {
  const { width } = useViewport();
  const { t } = useTranslation();
  const history = useNavigate();

  const handleRedirect = (tile) => {
    if (props.redirectTo === "myDesigns") {
      history(`/mydesign/${tile.id}`);
    } else if (props.redirectTo === "myExpariences") {
      history(`/myExpariences/${tile.id}`);
    } else if (props.redirectTo === "fanartzones") {
      window.open(tile.landingUrl, "_blank");
    } else if (props.redirectTo === "purchased-geoloc") {
      history(`/purchased-geoloc/${tile._id}`);
    } else if (props.redirectTo === "geolocalised-expariences") {
      history(`/purchase-geoloc/${tile.id}`);
    } else {
      console.log("no redirect");
    }
  };

  const getLabel = useCallback((tile) => {
    const myLabelCondition = tile.hasOwnProperty("displayName")
      ? "displayName"
      : "name";
    return {
      myDesigns: "label",
      myExpariences: myLabelCondition,
      "purchased-geoloc": myLabelCondition,
      "geolocalised-expariences": myLabelCondition,
      fanartzones: "name",
      classes: "name",
    };
  }, []);

  if (props.tiles?.length === 0) return null;
  else
    return (
      <Root>
        <div className={classes.elementContainer}>
          <div className={classes.titleContainer}>
            <Typography
              variant="h3"
              className={classes.categoryText}
              style={{
                padding: width < 1024 && "0 12px 0 0",
              }}
            >
              {props.loading || !props.tiles ? <Skeleton /> : props.title}
            </Typography>
            {props.redirectTo !== "classes" && (
              <Typography
                variant="body1"
                className={classes.seeMoreText}
                onClick={() => history(`/${props.redirectTo}`)}
                style={{
                  right: width < 1024 ? "12px" : "0",
                }}
              >
                {props.loading || !props.tiles ? (
                  <Skeleton />
                ) : (
                  t("userDashboard.seeMore")
                )}
              </Typography>
            )}
          </div>
          {props.loading || !props.tiles ? (
            <Skeleton variant="rectangular" className={classes.skeletonRoot} />
          ) : (
            <div className={classes.root}>
              {props.tiles.length === 0 ? (
                <Typography variant="body1" className={classes.noResults}>
                  {t("myExpariences.noResults")}
                </Typography>
              ) : (
                <ImageList className={classes.gridList}>
                  {props.tiles.map((tile) => {
                    let url = null;
                    const label = getLabel(tile);
                    if (props.redirectTo === "myDesigns") {
                      url =
                        process.env.REACT_APP_API_URL +
                        "/public/frames/" +
                        tile.id +
                        "/image";
                    } else if (
                      props.redirectTo === "myExpariences" ||
                      props.redirectTo === "fanartzones" ||
                      props.redirectTo === "publicExpariences" ||
                      props.redirectTo === "geolocalised-expariences" ||
                      props.redirectTo === "purchased-geoloc"
                    ) {
                      url = tile.logoUrl;
                    } else url = tile.imageUrl;
                    return (
                      <ImageListItem
                        key={tile._id || tile.id}
                        style={{ width: "inherit", marginRight: "0.5rem" }}
                        onClick={() => handleRedirect(tile)}
                      >
                        {props.redirectTo === "classes" && !url ? (
                          <Lottie
                            animationData={School}
                            loop={false}
                            style={{
                              width: "200px",
                              height: "180px",
                            }}
                          />
                        ) : url ? (
                          <img
                            src={url}
                            alt={tile[label[props.redirectTo]]}
                            className={classes.tileWidth}
                          />
                        ) : null}

                        <ImageListItemBar
                          title={tile[label[props.redirectTo]]}
                          classes={{
                            root: classes.titleBar,
                            title: classes.title,
                          }}
                          actionIcon={
                            props.redirectTo !== "classes" ? (
                              <IconButton
                                aria-label={`visualize ${
                                  tile[label[props.redirectTo]]
                                }`}
                                size="large"
                              >
                                <VisibilityIcon className={classes.title} />
                              </IconButton>
                            ) : null
                          }
                        />
                      </ImageListItem>
                    );
                  })}
                </ImageList>
              )}
            </div>
          )}
        </div>
      </Root>
    );
}
